import { firstValueFrom, Subject, Unsubscribable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  OpeningTimes,
  TimePoint,
  TimeRange,
  translateWeekDay,
  Weekday,
  weekDays,
} from '../lead.model';

interface SingleDayInterface {
  type: 'singleDay';
  weekday: Weekday;
}

interface DayIntervalInterface {
  type: 'dayInterval';
  start: Weekday;
  end: Weekday;
}

interface CompressedOpeningTimesInterface {
  weekday: SingleDayInterface | DayIntervalInterface;
  timeRange: TimeRange[];
}

export class CompressedOpeningTimes {
  openingTimes: OpeningTimes;

  private compressedOpeningTimes = new Subject<string>();
  private compressedOpeningTimes$ = this.compressedOpeningTimes.asObservable();

  constructor(

    private translateService: TranslateService,

    private initialOpeningTimes: OpeningTimes,

  ) {

    this.openingTimes = initialOpeningTimes;

  }

  public setNewOpeningTimes(newOpeningTimes: OpeningTimes) {
    this.openingTimes = newOpeningTimes;
    this.update();
  }

  public subscribe(func: (value: string) => void): Unsubscribable {
    const languageSubscription = this.translateService.onLangChange.subscribe(
      () => this.update(),
    );
    const openingTimesSubscription =
      this.compressedOpeningTimes$.subscribe(func);

    // Emit the current opening times to new subscribers
    void this.update();

    return {
      unsubscribe: () => {
        languageSubscription.unsubscribe();
        openingTimesSubscription.unsubscribe();
      },
    };
  }

  /**
   * This method is triggered when the openingTimes change or when the language changes
   * Then is recalculates the compressed openingTimes
   * @private
   */
  private async update() {
    const translatedWeekdays = await firstValueFrom(
      this.translateService.get(weekDays.map(translateWeekDay)),
    );
    const translateDay = (weekDay: Weekday) =>
      translatedWeekdays[translateWeekDay(weekDay)];
    const timeToString = (time: TimePoint) =>
      `${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}`;
    const output: CompressedOpeningTimesInterface[] = [];

    for (const openingDay of this.openingTimes.openingDays) {
      const pushCurrentDay = () =>
        output.push({
          weekday: { type: 'singleDay', weekday: openingDay.weekday },
          timeRange: openingDay.timeRange,
        });
      if (output.length == 0) {
        pushCurrentDay();
      } else {
        const last = output[output.length - 1];
        if (last.weekday.type == 'singleDay') {
          const lastDay = weekDays.indexOf(last.weekday.weekday);
          if (
            lastDay === weekDays.indexOf(openingDay.weekday) - 1 &&
            JSON.stringify(last.timeRange) ==
              JSON.stringify(openingDay.timeRange)
          ) {
            last.weekday = {
              type: 'dayInterval',
              start: last.weekday.weekday,
              end: openingDay.weekday,
            };
          } else {
            pushCurrentDay();
          }
        } else {
          const lastDay = weekDays.indexOf(last.weekday.end);
          if (
            lastDay === weekDays.indexOf(openingDay.weekday) - 1 &&
            JSON.stringify(last.timeRange) ==
              JSON.stringify(openingDay.timeRange)
          ) {
            last.weekday = {
              type: 'dayInterval',
              start: last.weekday.start,
              end: openingDay.weekday,
            };
          } else {
            pushCurrentDay();
          }
        }
      }
    }
    const compressedOpeningTimes = output
      .flatMap((compressedOpeningTimes) => {
        const timeRanges = compressedOpeningTimes.timeRange.map(
          (timeRange: TimeRange) =>
            timeToString(timeRange.start) + ' - ' + timeToString(timeRange.end),
        );
        if (compressedOpeningTimes.weekday.type === 'dayInterval') {
          return [
            translateDay(compressedOpeningTimes.weekday.start) +
              ' - ' +
              translateDay(compressedOpeningTimes.weekday.end),
          ].concat(timeRanges);
        } else {
          return [translateDay(compressedOpeningTimes.weekday.weekday)].concat(
            timeRanges,
          );
        }
      })
      .join('\n');
    this.compressedOpeningTimes.next(compressedOpeningTimes);
  }
}
